/**
 * Medchem Rule Lookup — merges built-in and user-defined rules.
 *
 * Handlers MUST consult these helpers rather than reading the built-in
 * arrays directly so user-added rules from `gibbs/preferences.js` are
 * honored automatically.
 *
 * Collision policy: built-in rules win over user rules with the same
 * `id` / `alertId` / `(fromRing, toRing)` key. The user rule stays in
 * storage but is logically shadowed; renaming the user rule's id will
 * un-shadow it.
 *
 * @module gibbs/knowledge/medchemRuleLookup
 */

import { ISOSTERE_RULES, TOXICOPHORE_RULES } from './medchemRules';
import { HETEROAROMATIC_SWAPS } from './compoundTransform';
import { loadRDKit } from './rdkitLoader';
import { getPreference, savePreference } from '../preferences';

/**
 * @typedef {Object} BioisostereRule
 * @property {string} id - Stable identifier (e.g., 'iso_cooh_tetrazole')
 * @property {string} fromPattern - SMARTS pattern matching the substructure to replace
 * @property {string} toFragment - SMILES of the replacement fragment
 * @property {string} [citation] - Literature citation
 * @property {string} [label] - Human-readable name (user rules)
 * @property {string} [source] - 'built-in' | 'user'
 */

/**
 * @typedef {Object} MitigationRule
 * @property {string} alertId - Toxicophore rule id (e.g., 'tox_aniline_dili')
 * @property {string} mitigationPattern - SMARTS recipe for the mitigation transform
 * @property {string} toFragment - SMILES of the replacement fragment
 * @property {string} [citation] - Literature citation
 * @property {string} [label] - Human-readable name (user rules)
 * @property {string} [source] - 'built-in' | 'user'
 */

/**
 * @typedef {Object} RingSwapRule
 * @property {string} fromRing - Ring system name (e.g., 'phenyl')
 * @property {string} toRing - Replacement ring name (e.g., 'pyridine')
 * @property {string} fromPattern - SMARTS for the source ring
 * @property {string} toFragment - SMILES for the replacement ring
 * @property {string} [citation]
 * @property {string} [label]
 * @property {string} [source] - 'built-in' | 'user'
 */

// ─── Bioisosteres ───────────────────────────────────────────────────────────

/**
 * Look up a bioisostere rule by id. Searches built-in ISOSTERE_RULES first,
 * then user-defined rules from preferences. Returns null if neither has it.
 *
 * Built-in rules with `fromPattern: null` (SMARTS not yet encoded) are
 * NOT returned — they're considered absent for runtime purposes. The
 * user can add their own with the same id to shadow the placeholder.
 *
 * @param {string} ruleId
 * @returns {BioisostereRule|null}
 */
export function getBioisostereByRuleId(ruleId) {
    if (!ruleId) return null;

    const builtIn = ISOSTERE_RULES.find(
        (r) => r.id === ruleId && r.fromPattern && r.toFragment,
    );
    if (builtIn) {
        return {
            id: builtIn.id,
            fromPattern: builtIn.fromPattern,
            toFragment: builtIn.toFragment,
            citation: builtIn.references && builtIn.references[0],
            source: 'built-in',
        };
    }

    const userRules = getPreference('userBioisosteres') || [];
    const userRule = userRules.find((r) => r.id === ruleId);
    if (userRule) {
        return { ...userRule, source: 'user' };
    }

    return null;
}

/**
 * Return all available bioisostere rules (built-in with non-null SMARTS +
 * user-defined). Useful for the Preferences UI display.
 *
 * @returns {BioisostereRule[]}
 */
export function listAllBioisosteres() {
    const builtIn = ISOSTERE_RULES
        .filter((r) => r.fromPattern && r.toFragment)
        .map((r) => ({
            id: r.id,
            fromPattern: r.fromPattern,
            toFragment: r.toFragment,
            citation: r.references && r.references[0],
            source: 'built-in',
        }));

    const userRules = (getPreference('userBioisosteres') || []).map(
        (r) => ({ ...r, source: 'user' }),
    );

    return [...builtIn, ...userRules];
}

// ─── Toxicophore mitigations ────────────────────────────────────────────────

/**
 * Look up a mitigation recipe for the given toxicophore alert id. Searches
 * built-in TOXICOPHORE_RULES first (where `mitigationPattern` is non-null),
 * then user-defined rules from preferences.
 *
 * @param {string} alertId
 * @returns {MitigationRule|null}
 */
export function getMitigationForAlert(alertId) {
    if (!alertId) return null;

    const builtIn = TOXICOPHORE_RULES.find(
        (r) => r.id === alertId && r.mitigationPattern && r.toFragment,
    );
    if (builtIn) {
        return {
            alertId: builtIn.id,
            mitigationPattern: builtIn.mitigationPattern,
            toFragment: builtIn.toFragment,
            // Propagate reactionSmarts so the client-side runReaction
            // path (gibbs-self-hosted-substructure-replace) sees it.
            reactionSmarts: builtIn.reactionSmarts || null,
            citation: builtIn.references && builtIn.references[0],
            source: 'built-in',
        };
    }

    const userRules = getPreference('userMitigations') || [];
    const userRule = userRules.find((r) => r.alertId === alertId);
    if (userRule) {
        return { ...userRule, source: 'user' };
    }

    return null;
}

// ─── Ring swaps ─────────────────────────────────────────────────────────────

/**
 * Look up a heteroaromatic ring-swap recipe. Searches built-in
 * HETEROAROMATIC_SWAPS first, then user-defined rules from preferences.
 *
 * @param {string} fromRing
 * @param {string} toRing
 * @returns {RingSwapRule|null}
 */
export function getRingSwap(fromRing, toRing) {
    if (!fromRing || !toRing) return null;

    const fromBuiltIn = HETEROAROMATIC_SWAPS[fromRing];
    const toBuiltIn = HETEROAROMATIC_SWAPS[toRing];
    if (fromBuiltIn && toBuiltIn) {
        return {
            fromRing,
            toRing,
            fromPattern: fromBuiltIn.pattern,
            toFragment: toBuiltIn.fragment,
            source: 'built-in',
        };
    }

    const userRules = getPreference('userRingSwaps') || [];
    const userRule = userRules.find(
        (r) => r.fromRing === fromRing && r.toRing === toRing,
    );
    if (userRule) {
        return { ...userRule, source: 'user' };
    }

    return null;
}

// ─── User-rule mutators ─────────────────────────────────────────────────────

/**
 * Append a user-defined bioisostere rule. Caller is responsible for
 * SMARTS validation before calling this (the Preferences UI does it
 * via RDKit-WASM parse-check).
 *
 * @param {Omit<BioisostereRule, 'source'>} rule
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function addUserBioisostere(rule) {
    const current = getPreference('userBioisosteres') || [];
    const next = [...current.filter((r) => r.id !== rule.id), rule];
    return savePreference('userBioisosteres', next);
}

/**
 * Remove a user-defined bioisostere rule by id.
 * @param {string} ruleId
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function removeUserBioisostere(ruleId) {
    const current = getPreference('userBioisosteres') || [];
    const next = current.filter((r) => r.id !== ruleId);
    return savePreference('userBioisosteres', next);
}

/**
 * Append a user-defined mitigation rule.
 * @param {Omit<MitigationRule, 'source'>} rule
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function addUserMitigation(rule) {
    const current = getPreference('userMitigations') || [];
    const next = [...current.filter((r) => r.alertId !== rule.alertId), rule];
    return savePreference('userMitigations', next);
}

/**
 * Remove a user-defined mitigation rule by alertId.
 * @param {string} alertId
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function removeUserMitigation(alertId) {
    const current = getPreference('userMitigations') || [];
    const next = current.filter((r) => r.alertId !== alertId);
    return savePreference('userMitigations', next);
}

/**
 * Append a user-defined ring-swap rule.
 * @param {Omit<RingSwapRule, 'source'>} rule
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function addUserRingSwap(rule) {
    const current = getPreference('userRingSwaps') || [];
    const next = [
        ...current.filter(
            (r) => !(r.fromRing === rule.fromRing && r.toRing === rule.toRing),
        ),
        rule,
    ];
    return savePreference('userRingSwaps', next);
}

/**
 * Remove a user-defined ring-swap rule by (fromRing, toRing) pair.
 * @param {string} fromRing
 * @param {string} toRing
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function removeUserRingSwap(fromRing, toRing) {
    const current = getPreference('userRingSwaps') || [];
    const next = current.filter(
        (r) => !(r.fromRing === fromRing && r.toRing === toRing),
    );
    return savePreference('userRingSwaps', next);
}

// ─── SMARTS validation (for the Preferences UI's Validate button) ──────────

/**
 * Parse-check a SMARTS pattern via RDKit-WASM. Returns
 * `{ valid: boolean, error: string|null }`. Never throws.
 *
 * Used by the Preferences UI's Validate button before saving a
 * user-defined rule. If RDKit cannot load (test/Node environment)
 * the result is `{ valid: false, error: 'RDKit unavailable' }` —
 * callers should offer a "Save anyway" affordance for offline use.
 *
 * @param {string} smarts
 * @returns {Promise<{ valid: boolean, error: string|null }>}
 */
export async function validateSmartsPattern(smarts) {
    if (!smarts || typeof smarts !== 'string') {
        return { valid: false, error: 'Empty SMARTS' };
    }

    let RDKit;
    try {
        RDKit = await loadRDKit();
    } catch (_) {
        return { valid: false, error: 'RDKit unavailable' };
    }

    if (!RDKit.isInitialized || !RDKit.isInitialized()) {
        return { valid: false, error: 'RDKit not initialized' };
    }

    let qmol = null;
    try {
        qmol = RDKit.getQMol(smarts);
        const ok = Boolean(qmol && (typeof qmol.is_valid !== 'function' || qmol.is_valid()));
        return { valid: ok, error: ok ? null : 'Invalid SMARTS' };
    } catch (err) {
        return { valid: false, error: err && err.message ? err.message : 'SMARTS parse failed' };
    } finally {
        if (qmol && qmol.delete) qmol.delete();
    }
}
